import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import DataSource from 'devextreme/data/data_source';
import {TrackerService} from '../stores/tracker.service';
import {UserDomainService} from '../user-domains/user-domain.service';
import {LayoutService} from '../home/layout.service';
import {durationToStr, TrailerIntervention, TrailerInterventionAlert, User} from 'shared';
import {TrailerInterventionService} from '../stores/trailer-intervention.service';
import {durationToHoursStr} from 'shared';
import CustomStore from 'devextreme/data/custom_store';
import emptyNotifyIcon from '@iconify/icons-mdi/alert-box';
import fuelIcon from '@iconify/icons-mdi/fuel';
import {DxDataGridComponent} from 'devextreme-angular';
import {InterventionsRequest} from './interventions-toolbar/interventions-toolbar.component';
import reportIcon from '@iconify/icons-mdi/chart-areaspline';
import {GoldenLayoutContainer} from 'ngx-golden-layout';
import GoldenLayout from 'golden-layout';

@Component({
  selector: 'app-trailer-interventions-grid',
  templateUrl: './trailer-interventions-grid.component.html',
  styleUrls: ['./trailer-interventions-grid.component.scss']
})
export class TrailerInterventionsGridComponent implements OnInit {
  ds: DataSource;
  canEdit: boolean;
  canAdd: boolean;
  canDelete: boolean;
  canAssign: boolean;
  alerts: TrailerInterventionAlert[];
  alertsIcon = emptyNotifyIcon;
  fuelIcon = fuelIcon;
  reportIcon = reportIcon;
  expandedAlertRowKey = 0;
  request: InterventionsRequest;
  dateFormat = 'shortDateShortTime';
  selectedIntervention: TrailerIntervention;
  reportVisible: boolean;

  @ViewChild(DxDataGridComponent, {static: true})
  grid: DxDataGridComponent;
  gridCss: { width: string; height: string };

  constructor(
    private trailerInterventionService: TrailerInterventionService,
    @Inject(GoldenLayoutContainer) private container: GoldenLayout.Container,
    private layoutService: LayoutService,
    public user: User,
  ) {

    // Permissions
    this.canEdit = user.can('edit trackers');

    // Bindings
    this.expandAlerts = this.expandAlerts.bind(this);
    this.expandFuel = this.expandFuel.bind(this);
    this.showInterventionDetails = this.showInterventionDetails.bind(this);

    // Dims
    this.gridCss = {
      width: this.container.getElement().width() + 'px',
      height: this.container.getElement().height() - 51 + 'px',
    };
  }

  accuracyRatingOptions = [
    { value: 'EXACT', label: 'Données exactes' },
    { value: 'APPROXIMATELY_CORRECT', label: 'Données approximativement correctes' },
    { value: 'INEXACT', label: 'Données inexactes' }
  ];

  ngOnInit(): void {
  }

  getAccuracyRatingLabel(rating: string): string {
    const option = this.accuracyRatingOptions.find(opt => opt.value === rating);
    return option ? option.label : rating;
  }

  getAccuracyRatingColor(rating: string): string {
    switch (rating) {
      case 'EXACT':
        return 'green';
      case 'APPROXIMATELY_CORRECT':
        return 'orange';
      case 'INEXACT':
        return 'red';
      default:
        return 'gray';
    }
  }

  hectareUnit(cellInfo) {
    return (parseFloat(cellInfo.value) / 10000).toFixed(2) + ' Ha';
  }

  widthUnit(cellInfo) {
    return (parseInt(cellInfo.value, 10) / 100).toFixed(1) + ' m';
  }

  percent(cellInfo) {
    if (!cellInfo.value) {
      return '-';
    }
    return (parseFloat(cellInfo.value) * 100).toFixed(2) + ' %';
  }

  duration(cellInfo) {
    return `<span style="text-decoration-style: dashed;" title="${durationToStr(cellInfo.value)}">${durationToHoursStr(cellInfo.value)} h</span>`;
  }

  durationToHoursStr(cellInfo) {
    return durationToHoursStr(cellInfo.value);
  }

  durationToStr(cellInfo) {
    return durationToStr(cellInfo.value);
  }

  onRowExpanding(e: any) {
    this.trailerInterventionService.alerts(e.key).subscribe(d => {
      this.alerts = d.data as TrailerInterventionAlert[];
    });
  }

  expandAlerts(e: any) {
    e.component.collapseAll(-1);
    if (this.expandedAlertRowKey === e.row.key) {
      this.expandedAlertRowKey = 0;
      return;
    }
    e.component.expandRow(e.row.key);
    this.expandedAlertRowKey = e.row.key;
  }

  expandFuel(e: any) {
    console.log(e);
    e.component.collapseAll(-1);
    e.component.expandRow(e.currentSelectedRowKeys[0]);
  }

  load(request: InterventionsRequest) {
    if (!this.ds) {
      // Data source
      this.ds = this.trailerInterventionService.newDataSource();
    }

    this.request = request;

    this.dateFormat = this.request.isToday ? 'shortTime' : 'shortDateShortTime';

    this.ds.filter([
      ['start_time', '>=', request.from],
      'and',
      ['start_time', '<=', request.to]
    ]);

    this.ds.reload();
  }

  showInterventionDetails(e: any) {
    this.selectedIntervention = e.hasOwnProperty('data') ? e.data : e.row.data;
    this.reportVisible = true;
  }

  setRatingValue(rowData: any, value: any) {
    rowData.data_accuracy_rating = value;
  }

  onRatingUpdate(e: any) {
    // Cancel the default update and handle rating update separately
    e.cancel = true;
    
    // Only handle rating updates
    if (e.newData && 'data_accuracy_rating' in e.newData && e.newData.data_accuracy_rating !== e.oldData.data_accuracy_rating) {
      const interventionId = e.key || e.oldData.id;
      const rating = e.newData.data_accuracy_rating;
      
      this.trailerInterventionService.updateDataAccuracyRating(interventionId, rating).subscribe({
        next: () => {
          // Rating updated successfully, update the row data
          if (e.data) {
            e.data.data_accuracy_rating = rating;
          }
          // Reload the grid to reflect the change
          if (this.ds) {
            this.ds.reload();
          }
        },
        error: (err) => {
          console.error('Error updating data accuracy rating:', err);
          // Revert the change in the grid
          if (e.data) {
            e.data.data_accuracy_rating = e.oldData.data_accuracy_rating;
          }
        }
      });
    }
  }
}
